/**
 * Users Screen
 *
 * Listagem e gerenciamento de usuários
 */
import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
	Shield,
	Trash2,
	Eye,
	Mail,
} from 'lucide-react'
import Header from '../organisms/Header'
import DataTable from '../organisms/DataTable'
import FilterBar from '../molecules/FilterBar'
import { Modal, ConfirmModal } from '../molecules/Modal'
import Button from '../atoms/Button'
import Select from '../atoms/Select'
import Badge from '../atoms/Badge'
import Avatar from '../atoms/Avatar'
import { LoadingScreen } from '../atoms/Spinner'
import { useUsers } from '../../core/admin.hooks'
import { usersApi } from '../../core/admin.api'
import { ADMIN_ROUTES, USER_ROLE_LABELS, USER_ROLE_COLORS } from '../../core/admin.constants'
import { formatDate, formatPhoneNumber } from '../../core/admin.utils'
import toast from 'react-hot-toast'

export default function UsersScreen() {
	const navigate = useNavigate()
	const [filters, setFilters] = useState({})
	const [selectedUser, setSelectedUser] = useState(null)
	const [showRoleModal, setShowRoleModal] = useState(false)
	const [showDeleteModal, setShowDeleteModal] = useState(false)
	const [newRole, setNewRole] = useState('user')
	const [actionLoading, setActionLoading] = useState(false)

	const {
		data,
		loading,
		page,
		setPage,
		setSearch,
		refetch,
	} = useUsers(filters)

	const users = data?.data || []
	const totalPages = data?.pagination?.totalPages || 1
	const total = data?.pagination?.total || 0

	// Filtros disponíveis
	const filterOptions = [
		{
			key: 'role',
			label: 'Tipo',
			type: 'select',
			options: [
				{ value: '', label: 'Todos' },
				{ value: 'user', label: 'Usuário' },
				{ value: 'admin', label: 'Admin' },
				{ value: 'superadmin', label: 'Super Admin' },
			],
		},
		{
			key: 'hasClub',
			label: 'Tem clube?',
			type: 'select',
			options: [
				{ value: '', label: 'Todos' },
				{ value: 'true', label: 'Sim' },
				{ value: 'false', label: 'Não' },
			],
		},
	]

	// Colunas da tabela
	const columns = [
		{
			key: 'user',
			label: 'Usuário',
			render: (row) => (
				<div className="flex items-center gap-2 md:gap-3 min-w-0">
					<Avatar
						src={row.avatar}
						name={row.name}
						size="sm"
						className="flex-shrink-0"
					/>
					<div className="min-w-0 flex-1">
						<p className="font-medium text-text text-sm truncate">
							{row.name} {row.lastname || ''}
						</p>
						<p className="text-xs text-text-body truncate">{row.email}</p>
					</div>
				</div>
			),
		},
		{
			key: 'phone',
			label: 'Telefone',
			render: (row) => (
				<span className="text-text-body text-sm">
					{row.whatsapp || row.phone ? formatPhoneNumber(row.whatsapp || row.phone) : '-'}
				</span>
			),
		},
		{
			key: 'role',
			label: 'Tipo',
			render: (row) => (
				<Badge variant={USER_ROLE_COLORS[row.role] || 'gray'} size="sm">
					{USER_ROLE_LABELS[row.role] || 'Usuário'}
				</Badge>
			),
		},
		{
			key: 'club',
			label: 'Clube',
			render: (row) => (
				<span className="text-text-body text-sm truncate block max-w-[120px]">
					{row.club?.name || '-'}
				</span>
			),
		},
		{
			key: 'createdAt',
			label: 'Cadastro',
			render: (row) => (
				<span className="text-text-muted text-xs">
					{formatDate(row.createdAt, 'dd/MM/yy')}
				</span>
			),
		},
	]

	// Ações do menu
	const getRowActions = (row) => [
		{
			icon: Eye,
			label: 'Ver detalhes',
			onClick: () => navigate(`${ADMIN_ROUTES.USERS}/${row._id}`),
		},
		{
			icon: Shield,
			label: 'Alterar tipo',
			onClick: () => {
				setSelectedUser(row)
				setNewRole(row.role || 'user')
				setShowRoleModal(true)
			},
		},
		{
			icon: Mail,
			label: 'Enviar e-mail',
			onClick: () => window.open(`mailto:${row.email}`, '_blank'),
		},
		{
			icon: Trash2,
			label: 'Excluir',
			onClick: () => {
				setSelectedUser(row)
				setShowDeleteModal(true)
			},
			variant: 'danger',
		},
	]

	// Handler para alterar role
	const handleChangeRole = async () => {
		if (!selectedUser) return

		setActionLoading(true)
		try {
			await usersApi.updateRole(selectedUser._id, newRole)
			toast.success(`Tipo alterado para ${USER_ROLE_LABELS[newRole]}`)
			setShowRoleModal(false)
			refetch()
		} catch (err) {
			toast.error(err.response?.data?.message || 'Erro ao alterar tipo')
		} finally {
			setActionLoading(false)
		}
	}

	// Handler para excluir
	const handleDelete = async () => {
		if (!selectedUser) return

		setActionLoading(true)
		try {
			await usersApi.delete(selectedUser._id)
			toast.success('Usuário excluído com sucesso')
			setShowDeleteModal(false)
			refetch()
		} catch (err) {
			toast.error(err.response?.data?.message || 'Erro ao excluir usuário')
		} finally {
			setActionLoading(false)
		}
	}

	if (loading && !data) {
		return (
			<div className="min-h-screen">
				<Header title="Usuários" />
				<LoadingScreen />
			</div>
		)
	}

	return (
		<div className="min-h-screen">
			<Header
				title="Usuários"
				subtitle={`${total} usuários cadastrados`}
			/>

			<div className="p-3 md:p-6 space-y-3 md:space-y-4">
				{/* Filtros */}
				<FilterBar
					filters={filterOptions}
					values={filters}
					onChange={setFilters}
					onSearch={setSearch}
					searchPlaceholder="Buscar por nome, email..."
				/>

				{/* Tabela */}
				<DataTable
					columns={columns}
					data={users}
					loading={loading}
					emptyMessage="Nenhum usuário encontrado"
					page={page}
					totalPages={totalPages}
					onPageChange={setPage}
					actions={getRowActions}
					mobileColumns={['user']}
				/>
			</div>

			{/* Modal de alterar tipo */}
			<Modal
				isOpen={showRoleModal}
				onClose={() => setShowRoleModal(false)}
				title="Alterar Tipo de Usuário"
			>
				<div className="space-y-4">
					<div className="flex items-center gap-3 mb-4">
						<Avatar
							src={selectedUser?.photo}
							name={selectedUser?.name}
							size="lg"
						/>
						<div>
							<p className="font-medium">
								{selectedUser?.name} {selectedUser?.lastname || ''}
							</p>
							<p className="text-sm text-text-body">{selectedUser?.email}</p>
						</div>
					</div>

					<Select
						label="Novo tipo"
						value={newRole}
						onChange={(e) => setNewRole(e.target.value)}
						options={[
							{ value: 'user', label: 'Usuário' },
							{ value: 'admin', label: 'Admin' },
							{ value: 'superadmin', label: 'Super Admin' },
						]}
					/>

					<div className="flex justify-end gap-3 mt-6">
						<Button
							variant="outline"
							onClick={() => setShowRoleModal(false)}
						>
							Cancelar
						</Button>
						<Button
							onClick={handleChangeRole}
							loading={actionLoading}
						>
							Salvar
						</Button>
					</div>
				</div>
			</Modal>

			{/* Modal de confirmação de exclusão */}
			<ConfirmModal
				isOpen={showDeleteModal}
				onClose={() => setShowDeleteModal(false)}
				onConfirm={handleDelete}
				title="Excluir Usuário"
				message={`Tem certeza que deseja excluir o usuário "${selectedUser?.name}"? Esta ação não pode ser desfeita.`}
				confirmText="Excluir"
				variant="danger"
				loading={actionLoading}
			/>
		</div>
	)
}
